import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { createInvoiceMissionRefund, getRefundableInvoices, InvoiceRefundRow } from 'src/shared/services/invoiceMissionService';

type Row = {
  invoiceId: number;
  customer: string;
  originalAmount: number;
  refundAmount: number;
};

type Props = {
  open: boolean;
  missionId: number;
  onClose: () => void;
  onCreated: (newMissionId: number) => void;
};

export const CreateInvoiceMissionRefundModal = (props: Props) => {
  const { open, missionId, onClose, onCreated } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [dueDate, setDueDate] = useState<string>(dayjs().add(10, 'day').format('YYYY-MM-DD'));
  const [bulkFromAmount, setBulkFromAmount] = useState<string>('');
  const [bulkToAmount, setBulkToAmount] = useState<string>('');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRefundableInvoices(missionId);
      const mapped: Row[] = res.data.map((inv: InvoiceRefundRow) => ({
        invoiceId: inv.invoiceId,
        customer: inv.customerName || `#${inv.customerId ?? ''}`,
        originalAmount: inv.amount ?? 0,
        refundAmount: inv.amount ?? 0,
      }));
      setRows(mapped);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:refund.loadError') as string);
    } finally {
      setLoading(false);
    }
  }, [missionId, t]);

  useEffect(() => {
    if (open) {
      fetchInvoices();
      setDueDate(dayjs().add(10, 'day').format('YYYY-MM-DD'));
    }
  }, [open, fetchInvoices]);

  const total = rows.reduce((sum, r) => sum + (r.refundAmount || 0), 0);
  const hasAnyRefund = rows.some((r) => r.refundAmount > 0);
  const anyOverMax = rows.some((r) => r.refundAmount > r.originalAmount);

  const handleAmountChange = (invoiceId: number, value: number) => {
    setRows((prev) => prev.map((r) => (r.invoiceId === invoiceId ? { ...r, refundAmount: value } : r)));
  };

  const handleFillZero = () => {
    if (rows.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('invoice:refund.confirmFillZero') as string)) return;
    setRows((prev) => prev.map((r) => ({ ...r, refundAmount: 0 })));
  };

  const handleFillFull = () => {
    if (rows.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('invoice:refund.confirmFillFull') as string)) return;
    setRows((prev) => prev.map((r) => ({ ...r, refundAmount: r.originalAmount })));
  };

  const handleBulkReplace = () => {
    if (bulkFromAmount === '' || bulkToAmount === '') return;
    const from = Number(bulkFromAmount);
    const to = Number(bulkToAmount);
    if (Number.isNaN(from) || Number.isNaN(to)) return;
    const affected = rows.filter((r) => r.refundAmount === from).length;
    if (affected === 0) {
      toast.info(t('invoice:refund.bulkReplaceNoMatch') as string);
      return;
    }
    setRows((prev) => prev.map((r) => (r.refundAmount === from ? { ...r, refundAmount: to } : r)));
    setBulkFromAmount('');
    setBulkToAmount('');
    toast.success(t('invoice:refund.bulkReplaceSuccess', { count: affected }) as string);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await createInvoiceMissionRefund(missionId, {
        dueDate: dueDate.replace(/-/g, ''),
        rows: rows.filter((r) => r.refundAmount > 0).map((r) => ({ invoiceId: r.invoiceId, amount: r.refundAmount })),
      });
      toast.success(t('invoice:refund.success') as string);
      onCreated(res.data.invoiceMission_id);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:refund.error') as string);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 600 }}>
        {t('invoice:refund.title')}
        <DialogCloseButton handleClose={onClose} />
      </DialogTitle>
      <Alert severity='info' sx={{ borderRadius: 0, borderTop: 1, borderColor: 'divider' }}>
        {t('invoice:refund.info')}
      </Alert>
      <DialogContent dividers>
        <Box sx={{ mb: 2, maxWidth: 240 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('invoice:dueDate')}
          </Typography>
          <DatePicker
            value={dueDate ? dayjs(dueDate) : null}
            onChange={(val) => {
              if (val && (!val.isValid() || val.year() < 1900)) return;
              setDueDate(val?.format('YYYY-MM-DD') || '');
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>

        <Typography variant='subtitle1' fontWeight='bold'>
          {t('invoice:refund.invoicesHeader')}
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />

        {!loading && rows.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: (thm) => thm.palette.grey[50], borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Button size='small' variant='outlined' onClick={handleFillZero}>
                {t('invoice:refund.fillZero')}
              </Button>
              <Button size='small' variant='outlined' onClick={handleFillFull}>
                {t('invoice:refund.fillFull')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {t('invoice:refund.bulkReplaceLabel')}
              </Typography>
              <TextField
                size='small'
                type='number'
                label={t('invoice:refund.bulkReplaceFrom')}
                value={bulkFromAmount}
                onChange={(e) => setBulkFromAmount(e.target.value)}
                sx={{ width: 130 }}
                InputProps={{ endAdornment: <InputAdornment position='end'>{currency}</InputAdornment> }}
              />
              <TextField
                size='small'
                type='number'
                label={t('invoice:refund.bulkReplaceTo')}
                value={bulkToAmount}
                onChange={(e) => setBulkToAmount(e.target.value)}
                sx={{ width: 130 }}
                InputProps={{ endAdornment: <InputAdornment position='end'>{currency}</InputAdornment> }}
              />
              <Button
                size='small'
                variant='contained'
                disableElevation
                onClick={handleBulkReplace}
                disabled={bulkFromAmount === '' || bulkToAmount === ''}
              >
                {t('invoice:refund.bulkReplaceExecute')}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, flex: 1 }}>
            {t('invoice:refund.customer')}
          </Typography>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, width: 140 }}>
            {t('invoice:refund.originalAmount')}
          </Typography>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, width: 160 }}>
            {t('invoice:refund.refundAmount')}
          </Typography>
        </Box>

        {loading && (
          <>
            <Skeleton variant='rectangular' height={48} sx={{ mb: 1 }} />
            <Skeleton variant='rectangular' height={48} sx={{ mb: 1 }} />
            <Skeleton variant='rectangular' height={48} />
          </>
        )}

        {!loading && rows.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
            {t('invoice:refund.noInvoices')}
          </Typography>
        )}

        {!loading &&
          rows.map((row) => (
            <Box key={row.invoiceId} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }}>
                {row.customer}
              </Typography>
              <Box sx={{ width: 140 }}>
                <AmountWithCurrency amount={row.originalAmount} />
              </Box>
              <TextField
                size='small'
                type='number'
                value={row.refundAmount}
                onChange={(e) => handleAmountChange(row.invoiceId, Number(e.target.value))}
                sx={{ width: 160, flexShrink: 0 }}
                inputProps={{ min: 0, max: row.originalAmount }}
                error={row.refundAmount > row.originalAmount}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
                }}
              />
            </Box>
          ))}

        <Divider sx={{ mt: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Typography variant='body1' fontWeight='bold'>
            {t('invoice:refund.total')}: <AmountWithCurrency amount={total} />
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={saving}>
          {t('common:button.cancel')}
        </Button>
        <Button variant='contained' disableElevation onClick={handleSave} disabled={!hasAnyRefund || anyOverMax || saving || loading}>
          {t('invoice:refund.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
